/**
 * Overview: an index of the filled sections (links to their headings), then those sections in order. Empty template
 * sections are not shown; an internal section carries the text label "Internal" (only readers with
 * `asset.view.internal` receive one at all).
 */

import { FileText } from 'lucide-react';
import { Link } from 'react-router-dom';

import type { AssetOut, SectionOut } from '@/api/types';
import { Badge } from '@/ui/Badge';
import { Markdown } from '@/ui/Markdown';
import { StateCard } from '@/ui/StateCard';
import { useSession } from '@/session/SessionContext';

export const sectionAnchor = (key: string) => `asset-section-${key}`;

export function filledSections(sections: SectionOut[]): SectionOut[] {
  return sections.filter((s) => s.body.trim() !== '').sort((a, b) => a.position - b.position);
}

export function OverviewTab({ asset }: { asset: AssetOut }) {
  const { can } = useSession();
  const sections = filledSections(asset.sections);
  if (sections.length === 0) {
    return (
      <StateCard icon={<FileText aria-hidden="true" />} title="No sections written yet">
        <p>{can('asset.manage') ? 'Open the editor to write the suggested sections.' : 'The experts have not described this asset yet.'}</p>
        {can('asset.manage') && (
          <p className="empty-actions"><Link className="btn btn-secondary" to={`/assets/${asset.id}/edit`}>Edit</Link></p>
        )}
      </StateCard>
    );
  }
  return (
    <div className="asset-overview">
      <nav className="asset-index" aria-label="Sections">
        <ol>
          {sections.map((s) => (
            <li key={s.key}>
              <a href={`#${sectionAnchor(s.key)}`}>{s.title}</a>
              {s.audience === 'internal' && <> <Badge variant="audience" value="internal" /></>}
            </li>
          ))}
        </ol>
      </nav>
      <div className="asset-sections">
        {sections.map((s) => (
          <section key={s.key} id={sectionAnchor(s.key)} className="asset-section" aria-labelledby={`${sectionAnchor(s.key)}-h`}>
            <h2 id={`${sectionAnchor(s.key)}-h`}>
              {s.title}
              {s.audience === 'internal' && <> <Badge variant="audience" value="internal" /></>}
            </h2>
            <div className="doc-sections-prose">
              <Markdown headingLevel={3}>{s.body}</Markdown>
            </div>
          </section>
        ))}
      </div>
    </div>
  );
}
